/**
 * Authentication Store
 *
 * Lightweight auth store using localStorage + axios.
 * No Redux dependency — designed for React Context consumption.
 *
 * Generated by portal_generator.py
 */

import axios from 'axios';
import type {
  User,
  TokenPayload,
  LoginCredentials,
  LoginResponse,
  RefreshTokenResponse,
} from './types';

const AUTH_BASE_URL = '/api/v1/auth';

const TOKEN_KEYS = {
  access: 'access_token',
  refresh: 'refresh_token',
  expiry: 'token_expiry',
  tenant: 'tenant_id',
} as const;

// ---------------------------------------------------------------------------
// JWT Decoder
// ---------------------------------------------------------------------------

function decodeJWT(token: string): User | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed: TokenPayload = JSON.parse(decoded);

    return {
      id: parsed.sub || '',
      username: parsed.username || '',
      role: parsed.role || (Array.isArray(parsed.roles) ? parsed.roles[0] : '') || '',
      permissions: Array.isArray(parsed.permissions) ? parsed.permissions : [],
      tenant_id: parsed.tenant_id || parsed.tenant || null,
      billing_entity_grants: Array.isArray(parsed.billing_entity_grants) ? parsed.billing_entity_grants : [],
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Token Storage
// ---------------------------------------------------------------------------

export const tokenStorage = {
  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEYS.access);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(TOKEN_KEYS.refresh);
  },

  getExpiry(): number | null {
    const v = localStorage.getItem(TOKEN_KEYS.expiry);
    return v ? parseInt(v, 10) : null;
  },

  store(accessToken: string, refreshToken: string, expiresIn: number, tenantId?: string | null): void {
    localStorage.setItem(TOKEN_KEYS.access, accessToken);
    localStorage.setItem(TOKEN_KEYS.refresh, refreshToken);
    const expiry = Math.floor(Date.now() / 1000) + expiresIn;
    localStorage.setItem(TOKEN_KEYS.expiry, String(expiry));
    // Generated entity-service clients (e.g. setup_matter.service.ts) read
    // localStorage.tenant_id directly to attach the X-Tenant-ID header on every
    // CRUD request. Without this, queries fall through to a 00000000-... default
    // and the multi-tenant filter returns zero rows.
    if (tenantId) {
      localStorage.setItem(TOKEN_KEYS.tenant, tenantId);
    }
  },

  clear(): void {
    localStorage.removeItem(TOKEN_KEYS.access);
    localStorage.removeItem(TOKEN_KEYS.refresh);
    localStorage.removeItem(TOKEN_KEYS.expiry);
    localStorage.removeItem(TOKEN_KEYS.tenant);
    // SEC-46-09: clear search history on logout to prevent cross-session PII leakage
    localStorage.removeItem('jylaw.globalSearch.recent');
    // VL-41-09: clear proposal pipeline filter on logout (prevents cross-session state leakage)
    localStorage.removeItem('pipeline.salesOnly');
    // SEC-48-07: clear spec-48 nav cutover snackbar flag (prevents cross-session suppression)
    localStorage.removeItem('jylaw_nav_cutover_v48_dismissed');
    // SEC-49-06: clear spec-49 C3 retirement snackbar flag (C3RetirementSnackbar FLAG_KEY)
    localStorage.removeItem('jy_c3_snackbar_dismissed');
    // SEC-49-06: clear spec-49 legacy cleanup deferred contract set (legacyCleanup DEFERRED_KEY)
    // (Cannot import DEFERRED_KEY directly — legacyCleanup.ts imports tokenStorage → circular)
    localStorage.removeItem('jy_legacy_cleanup_deferred');
  },

  isExpired(): boolean {
    const expiry = tokenStorage.getExpiry();
    if (!expiry) return true;
    return Math.floor(Date.now() / 1000) >= expiry;
  },

  isExpiringSoon(bufferSeconds = 300): boolean {
    const expiry = tokenStorage.getExpiry();
    if (!expiry) return true;
    return (expiry - Math.floor(Date.now() / 1000)) < bufferSeconds;
  },
};

// ---------------------------------------------------------------------------
// Auth Actions
// ---------------------------------------------------------------------------

/**
 * Build the POST /api/v1/auth/login request body.
 *
 * Exported for unit-testing the surface derivation logic without mocking axios.
 * The `surface` field tells auth-service which auth surface the caller is using;
 * auth-service enforces role ↔ surface matching and returns 401 on mismatch.
 *
 * Derivation rule:
 *   - If `expectedAud` is set, derive surface from it.
 *   - If no `expectedAud` (caller didn't pass options), default to 'jylaw_internal'
 *     to preserve backwards compatibility for existing internal-SPA sessions.
 */
export function buildLoginPayload(
  credentials: LoginCredentials,
  options?: { expectedAud?: 'jylaw_internal' | 'client_portal' },
): LoginCredentials & { surface: 'jylaw_internal' | 'client_portal' } {
  const surface: 'jylaw_internal' | 'client_portal' =
    options?.expectedAud ?? credentials.surface ?? 'jylaw_internal';
  return {
    ...credentials,
    tenant_id: credentials.tenant_id || '11111111-1111-1111-1111-111111111111',
    surface,
  };
}

export async function loginAction(
  credentials: LoginCredentials,
  options?: { expectedAud?: 'jylaw_internal' | 'client_portal' },
): Promise<{ user: User; response: LoginResponse }> {
  const payload = buildLoginPayload(credentials, options);
  const res = await axios.post<LoginResponse>(
    `${AUTH_BASE_URL}/login`,
    payload,
    { headers: { 'Content-Type': 'application/json' } },
  );

  const data = res.data;
  if (!data.access_token) {
    throw new Error('Invalid login response: missing access_token');
  }

  // ── Audience + role gate ────────────────────────────────────────────────
  // Reject tokens whose aud / role don't match the expected surface.
  // This prevents internal users logging into /client/login and vice versa.
  if (options?.expectedAud) {
    const parts = data.access_token.split('.');
    let claims: Record<string, unknown> = {};
    try {
      claims = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      throw new Error('Failed to parse token claims');
    }
    const tokenAud = claims?.aud as string | undefined;
    const tokenRole = (claims?.role as string | undefined) ??
      (Array.isArray(claims?.roles) ? (claims.roles as string[])[0] : undefined);

    if (options.expectedAud === 'jylaw_internal') {
      if (tokenAud === 'client_portal' || tokenRole === 'client_portal_user') {
        throw new Error(
          'هذا الحساب مخصص لبوابة العملاء — استخدم /client/login | This account is for the client portal — use /client/login',
        );
      }
    } else if (options.expectedAud === 'client_portal') {
      if (tokenAud !== 'client_portal' || tokenRole !== 'client_portal_user') {
        throw new Error(
          'هذا الحساب داخلي — استخدم /login | This is an internal account — use /login',
        );
      }
    }
  }
  // ────────────────────────────────────────────────────────────────────────

  const user = decodeJWT(data.access_token);
  if (!user) {
    throw new Error('Failed to decode user from JWT');
  }
  tokenStorage.store(data.access_token, data.refresh_token, data.expires_in, user.tenant_id);

  return { user, response: data };
}

export async function logoutAction(): Promise<void> {
  const token = tokenStorage.getAccessToken();
  try {
    await axios.post(
      `${AUTH_BASE_URL}/logout`,
      {},
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
  } catch {
    // Ignore backend errors — still clear local state
  }
  tokenStorage.clear();
}

export async function refreshAction(): Promise<User> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const res = await axios.post<RefreshTokenResponse>(
    `${AUTH_BASE_URL}/refresh`,
    { refresh_token: refreshToken },
  );

  if (!res.data.access_token) {
    throw new Error('Invalid refresh response');
  }

  const user = decodeJWT(res.data.access_token);
  if (!user) {
    throw new Error('Failed to decode refreshed JWT');
  }
  tokenStorage.store(res.data.access_token, refreshToken, res.data.expires_in, user.tenant_id);

  return user;
}

/** Restore auth from localStorage (page reload) */
export function restoreAuth(): User | null {
  const token = tokenStorage.getAccessToken();
  if (!token || tokenStorage.isExpired()) {
    tokenStorage.clear();
    return null;
  }
  return decodeJWT(token);
}
